import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import type { BaseIconProps } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";
import { TextWrapper } from "@/components/util/TextWrapper";
import { Slot } from "@radix-ui/react-slot";
import {
    createContext,
    forwardRef,
    useContext,
    type ButtonHTMLAttributes,
    type HTMLAttributes,
    type ReactNode,
} from "react";

export type ButtonGroupVariant = "primary" | "secondary" | "ghost";

const ButtonGroupContext = createContext<{
    variant: ButtonGroupVariant;
} | null>(null);

export type ButtonGroupProps = {
    variant?: ButtonGroupVariant;
    className?: string;
    children: ReactNode;
    asChild?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export type ButtonGroupItemProps = {
    children?: ReactNode;
    LeadingIcon?: React.ComponentType<BaseIconProps>;
    disabled?: boolean;
    loading?: boolean;
    className?: string;
    asChild?: boolean;
    _disableTranslateWrapping?: boolean;
    disableTracking?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
    ({ className, children, asChild = false, variant, ...props }, ref) => {
        const Comp = asChild ? Slot : "div";

        return (
            <Comp ref={ref} className={cn("inline-flex", className)} role="group" {...props}>
                <ButtonGroupContext.Provider value={{ variant: variant ?? "primary" }}>
                    {children}
                </ButtonGroupContext.Provider>
            </Comp>
        );
    },
);
ButtonGroup.displayName = "ButtonGroup";

export const ButtonGroupItem = forwardRef<HTMLButtonElement, ButtonGroupItemProps>(
    (
        {
            className,
            asChild = false,
            LeadingIcon,
            loading,
            disabled,
            children,
            disableTracking,
            _disableTranslateWrapping,
            ...props
        },
        ref,
    ) => {
        const context = useContext(ButtonGroupContext);
        if (!context) {
            throw new Error("ButtonGroupItem must be used within a ButtonGroup");
        }
        const { variant = "primary" } = context;

        const Comp = asChild ? Slot : "button";

        // Base button styles from Button component (md size)
        const baseStyles = cn(
            "px-2 py-1.5 min-h-8",
            "select-none",
            "inline-flex items-center gap-2 text-sm font-medium justify-center whitespace-nowrap",
            "disabled:opacity-50 disabled:pointer-events-none",
            "focus:ring-0",
            "focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:animate-focus-pulse motion-reduce:animate-none focus-visible:outline-border-brand active:outline-0",
            // Remove rounded corners from middle items
            "rounded-none",
            // Add rounded corners only to first/last items
            "first:rounded-l-lg last:rounded-r-lg",
        );

        // Variant-specific styles
        const variantStyles = cn(
            variant === "primary" && [
                "text-content-primary-inverted hover:text-content-accent-inverted active:text-content-accent-inverted data-[state=open]:text-content-accent-inverted focus-visible:text-content-accent-inverted",
                "bg-surface-button-primary hover:bg-surface-button-primary-accent active:bg-surface-button-primary-accent data-[state=open]:bg-surface-button-primary-accent focus-visible:bg-surface-button-primary-accent",
            ],
            variant === "secondary" && [
                "text-content-primary bg-surface-button-secondary hover:bg-surface-button-secondary-accent active:bg-surface-button-secondary-accent data-[state=open]:bg-surface-button-secondary-accent",
            ],
            variant === "ghost" && [
                "text-content-primary hover:text-content-accent active:text-content-accent data-[state=open]:text-content-accent focus-visible:text-content-accent",
                "bg-surface-button-clear hover:bg-surface-button-clear-accent data-[state=open]:bg-surface-button-clear-accent focus-visible:bg-surface-button-clear-accent",
            ],
        );

        let leftElement;
        if (loading) {
            leftElement = <IconSpinner className="animate-spin" size="base" aria-hidden={true} />;
        } else if (LeadingIcon) {
            leftElement = <LeadingIcon size="base" aria-hidden={true} />;
        }

        return (
            <TextWrapper
                asChild={asChild}
                leftElement={leftElement}
                className={cn(baseStyles, variantStyles, className)}
                disableTracking={disableTracking}
                _disableTranslateWrapping={_disableTranslateWrapping}
                ref={ref}
                disabled={disabled || loading}
                aria-busy={loading}
                as={Comp as React.ElementType}
                {...props}
            >
                {children}
            </TextWrapper>
        );
    },
);
ButtonGroupItem.displayName = "ButtonGroupItem";
